import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GlobalService } from '../../../../../global/global.service';
import { IAdvancedFilterListItem, IAdvanceFilter } from '../../../../shared/advanced-filters/components/advance-filter';
import { IGetSavedListingFiltersResponseDTO, SavedFilterType } from '../../../../shared/save-listing-filters/save-filters.dto';
import * as SavedFiltersActions from '../../../../shared/save-listing-filters/store/saved-filters.actions';
import * as savedFiltersActions from '../../../../shared/save-listing-filters/store/saved-filters.actions';
import { SaveListingFilters } from '../../../../shared/save-listing-filters/store/saved-filters.facade';
import * as SavedFiltersSelectors from '../../../../shared/save-listing-filters/store/saved-filters.selectors';
import { ContractControlAdvanceFilterParamType, } from '../../advanced-filters';
import { ICCSaveNewFilter } from '../../contract-control-save-filter';
import * as contractControlAction from '../actions/contract-control.action';

import { IExportContractControlRequest } from '../dto/export-request';

import { ContractControlEconomicsService } from '../../contract-control-economics-graph/contract-control-economics.service';
import * as contractControlSelector from '../selectors/contract-control.selector';
import { IColumnSettings } from 'app/main/shared/shared/table/components/custom-columns-settings/column-settings';

@Injectable({
    providedIn: 'root'
})
export class ContractControlFacade {
    
    public advancedFiltersList$: Observable<IAdvancedFilterListItem<ContractControlAdvanceFilterParamType>[]> = this.store.select(contractControlSelector.advancedFilterList);
    public isAdvancedFiltersListLoading$: Observable<boolean> = this.store.select(contractControlSelector.isAdvancedFiltersListLoading);
    public savedFilters$: Observable<IGetSavedListingFiltersResponseDTO[]> = this.store.select(SavedFiltersSelectors.filtersByType(SavedFilterType.Contract));
    public appliedAdvancedFilters$: Observable<IAdvanceFilter<ContractControlAdvanceFilterParamType>[]> = this.store.select(contractControlSelector.appliedAdvancedFilters);
    public selectedSavedFilterId$: Observable<number> = this.store.select(SavedFiltersSelectors.selectedFilter).pipe(map(x=>x?.id || null));



    
    /**
     *
     * @param store
     * @param globalService
     * @param saveListingFiltersFacade
     * @param contractControlEmitter
     * @param transloco
     */
    constructor(
        private store: Store,
        private globalService: GlobalService,
        private saveListingFiltersFacade: SaveListingFilters,
        private transloco: TranslocoService,
        private _contractControlEconomicsService: ContractControlEconomicsService,
    ) { }



    exportData(exportOptions: IExportContractControlRequest) {
        this.store.dispatch(contractControlAction.exportContractControlData({exportOptions}));
    }


    /**
     * Dispatch clear user listing action
     */
    clearStore(): void {
        this.store.dispatch(contractControlAction.resetState());
        this.store.dispatch(SavedFiltersActions.resetState());
    }


    fetchAdvancedFilters() {
        this.store.dispatch(contractControlAction.fetchAdvancedFilters());
    }

    advancedFilterSearch(searchString: string) {
        this.store.dispatch(contractControlAction.advancedFilterSearch({searchString}));
    }

    addNewFilter(filter: Omit<IAdvanceFilter<ContractControlAdvanceFilterParamType>, "uuid">) {
        this.store.dispatch(contractControlAction.addAdvancedFilter({filter}));
    }

    onRemoveFilter(filter: IAdvanceFilter<ContractControlAdvanceFilterParamType>) {
        this.store.dispatch(contractControlAction.removeAdvancedFilter({filter}));
    }

    restoreFilter(filterId: number) {
        this.store.dispatch(contractControlAction.restoreFilter({filterId}));
    }

    onFilterClose(filter: Partial<IAdvanceFilter<ContractControlAdvanceFilterParamType>>) {
        this.store.dispatch(contractControlAction.onFilterClose({filter}));
    }

    updateAdvancedFilter(filter: IAdvanceFilter<ContractControlAdvanceFilterParamType>) {
        this.store.dispatch(contractControlAction.updateAdvancedFilter({filter}));
    }

    fetchSavedFilters(): void {
        this.saveListingFiltersFacade.fetchSavedFilters();
    }

    saveFilter(filterParams: ICCSaveNewFilter) {
        if (filterParams?.value) {
            const params = {...filterParams?.value};
            delete params.page;

            if (Object.keys(params).length > 0) {
                this.saveListingFiltersFacade.saveFilter({
                    ...filterParams,
                    type: SavedFilterType.Contract,
                    value: params
                });
            } else {
                this.globalService.toastrWarning(this.transloco.translate('No filter applied toast (saved filters modal) - You have to apply some filters first'))
            }
        } else {
            this.globalService.toastrWarning(this.transloco.translate('No filter applied toast (saved filters modal) - You have to apply some filters first'))
        }
    }

    editFilter(filter: IGetSavedListingFiltersResponseDTO) {
        this.store.dispatch(contractControlAction.editFilter({filter}));
    }

    deleteFilter(filterId: number) {
        this.store.dispatch(contractControlAction.deleteFilter({filterId}));
    }

    setAdvancedFilters(filters: Partial<IAdvanceFilter<ContractControlAdvanceFilterParamType>[]>) {
        this.store.dispatch(contractControlAction.setAdvancedDocumentFilters({filters}))
    }

    clearSavedFilterSelection() {
        this.store.dispatch(savedFiltersActions.clearSelectedFilter());
    }

    clearFiltersOnDestroy(){
        this.store.dispatch(contractControlAction.clearAdvancedFiltersOnDestroy());
        this.store.dispatch(savedFiltersActions.clearSelectedFilter());
    }

    public customColumnSettings$ = this.store.select(contractControlSelector.customColumnSettings);

    fetchCustomColumnSettings() {
        this.store.dispatch(contractControlAction.fetchCustomColumnSettings());
    }

    setCustomColumnSettingsDetails(columnSettings: IColumnSettings[]) {
        this.store.dispatch(contractControlAction.setCustomColumnSettings({ columnSettings }));
    }

}
